import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Table, Typography } from 'antd';
import { isDashboardContactsModalOpen } from '../../../../../helpers/dashboardViewerWorkGate';
import treeCartService, {
    formatTreeCutOptionCardLabel,
    getTreeCutCostWithParams,
    TREE_CART_DEFAULT_LOCATION,
    TREE_CUT_CARD_DEFAULT_OPTION_ID,
    TREE_CUT_COMPARE_UNIFIED_OPTIONS,
    type TreeCutCompareUnifiedOption,
    type TreeCutExcelParams,
} from '../../../../../services/treeCartService';
import DashboardModal from '../DashboardModal';
import '../oneButton-css/OneButtonFormControls.css';

export interface TreeCutAlternateCutCostsModalProps {
    open: boolean;
    onClose: () => void;
    theme?: 'dark' | 'light';
    sceneViewer: unknown;
    treeIds: string[];
    scenarioLabel: string;
    scenarioIndex: number;
    defaultCutCostOnCard: number | null;
    selectedOptionId?: string | null;
    onApplyCutOption: (payload: {
        scenarioIndex: number;
        optionId: string;
        location: string;
        serviceLabel: string;
        total: number | null;
        params: TreeCutExcelParams;
    }) => void;
}

type CompareRow = {
    key: string;
    location: string;
    serviceLabel: string;
    isCardDefault: boolean;
    total: number | null;
    perTree: Array<{ treeId: string; cost: number | null }>;
    params: TreeCutExcelParams;
};

function resolveTreesFromViewer(sceneViewer: unknown, treeIds: string[]): { trees: any[]; missingIds: string[] } {
    const sv = sceneViewer as { treeGroup?: { children?: any[] } } | null | undefined;
    const children = sv?.treeGroup?.children ?? [];
    const trees: any[] = [];
    const missingIds: string[] = [];
    for (const id of treeIds) {
        const t = children.find((g: any) => g?.userData?.treeId === id);
        if (t && typeof t.userData?.height === 'number') trees.push(t);
        else missingIds.push(id);
    }
    return { trees, missingIds };
}

async function buildUnifiedRows(token: string, trees: any[], options: TreeCutCompareUnifiedOption[]): Promise<CompareRow[]> {
    const built: CompareRow[] = [];
    for (const opt of options) {
        const perTree: Array<{ treeId: string; cost: number | null }> = [];
        for (const tree of trees) {
            const id = String(tree.userData?.treeId ?? '');
            const cost = await getTreeCutCostWithParams(token, tree, opt.params);
            perTree.push({ treeId: id, cost });
            await new Promise((r) => setTimeout(r, 40));
        }
        const nums = perTree.map((p) => p.cost).filter((c): c is number => c != null && Number.isFinite(c));
        const total = nums.length === perTree.length ? nums.reduce((a, b) => a + b, 0) : null;
        built.push({
            key: opt.id,
            location: opt.location,
            serviceLabel: opt.serviceLabel,
            isCardDefault: opt.isCardDefault,
            total,
            perTree,
            params: opt.params,
        });
    }
    return built;
}

export function TreeCutAlternateCutCostsModal({
    open,
    onClose,
    theme = 'dark',
    sceneViewer,
    treeIds,
    scenarioLabel,
    scenarioIndex,
    defaultCutCostOnCard,
    selectedOptionId,
    onApplyCutOption,
}: TreeCutAlternateCutCostsModalProps) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [rows, setRows] = useState<CompareRow[]>([]);
    const [pendingOptionId, setPendingOptionId] = useState<string | null>(null);

    const activeOptionId = pendingOptionId ?? selectedOptionId ?? TREE_CUT_CARD_DEFAULT_OPTION_ID;

    const loadPresets = useCallback(async () => {
        if (!open || treeIds.length === 0) return;
        if (isDashboardContactsModalOpen()) return;
        setLoading(true);
        setError(null);
        setRows([]);

        const { trees, missingIds } = resolveTreesFromViewer(sceneViewer, treeIds);
        if (missingIds.length > 0) {
            setError(
                `These trees were not found in the current 3D scene (or height is missing): ${missingIds.join(', ')}. ` +
                    'Reload the site or re-run tree simulation.',
            );
            setLoading(false);
            return;
        }

        const token = await treeCartService.getExcelServiceToken();
        if (typeof token !== 'string') {
            setError('Could not get Excel service token for Tree Calculator.');
            setLoading(false);
            return;
        }

        try {
            const next = await buildUnifiedRows(token, trees, TREE_CUT_COMPARE_UNIFIED_OPTIONS);
            setRows(next);
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to estimate cut costs');
        } finally {
            setLoading(false);
        }
    }, [open, treeIds, sceneViewer]);

    useEffect(() => {
        void loadPresets();
    }, [loadPresets]);

    useEffect(() => {
        if (!open) {
            setPendingOptionId(null);
            return;
        }
        setPendingOptionId(selectedOptionId ?? TREE_CUT_CARD_DEFAULT_OPTION_ID);
    }, [open, selectedOptionId]);

    const pendingRow = useMemo(
        () => rows.find((r) => r.key === activeOptionId) ?? null,
        [rows, activeOptionId],
    );

    const cardUsesCustomOption = Boolean(selectedOptionId && selectedOptionId !== TREE_CUT_CARD_DEFAULT_OPTION_ID);

    const cardOptionLabel = useMemo(() => {
        if (!cardUsesCustomOption) return formatTreeCutOptionCardLabel(null);
        const onCard = rows.find((r) => r.key === selectedOptionId);
        return formatTreeCutOptionCardLabel({
            cutCostLocation: onCard?.location,
            cutCostServiceLabel: onCard?.serviceLabel,
            cutCostOptionId: selectedOptionId,
        });
    }, [cardUsesCustomOption, rows, selectedOptionId]);

    const handleApply = () => {
        if (!pendingRow) return;
        onApplyCutOption({
            scenarioIndex,
            optionId: pendingRow.key,
            location: pendingRow.location,
            serviceLabel: pendingRow.serviceLabel,
            total: pendingRow.total,
            params: pendingRow.params,
        });
        onClose();
    };

    const columns = [
        {
            title: 'Location (B3)',
            dataIndex: 'location',
            key: 'location',
            width: 130,
            render: (loc: string, r: CompareRow) => (
                <span>
                    <strong>{loc}</strong>
                    {loc === TREE_CART_DEFAULT_LOCATION ? (
                        <span style={{ display: 'block', fontSize: 11, fontWeight: 400, opacity: 0.8, marginTop: 2 }}>
                            AI default location
                        </span>
                    ) : null}
                    {r.key === activeOptionId ? (
                        <span className="obd-tree-cut-compare-modal__row-tag obd-tree-cut-compare-modal__row-tag--selected">
                            {r.key === selectedOptionId || (r.isCardDefault && !cardUsesCustomOption) ? '= on card' : 'Selected'}
                        </span>
                    ) : r.isCardDefault && !cardUsesCustomOption ? (
                        <span style={{ display: 'block', fontSize: 11, fontWeight: 500, opacity: 0.75, marginTop: 2 }}>
                            AI default preset
                        </span>
                    ) : null}
                </span>
            ),
        },
        {
            title: 'Stump & haul',
            dataIndex: 'serviceLabel',
            key: 'serviceLabel',
            ellipsis: true,
            render: (text: string, r: CompareRow) => (
                <div>
                    <div>{text}</div>
                    <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>
                        {r.params.stump} · haul {r.params.haul} · DBH {r.params.dbhStr} · {r.params.noOfObst} obst.
                    </div>
                </div>
            ),
        },
        {
            title: 'Total cut cost',
            key: 'total',
            width: 130,
            align: 'right' as const,
            render: (_: unknown, r: CompareRow) =>
                r.total != null && Number.isFinite(r.total) ? (
                    <strong>${Math.round(r.total).toLocaleString()}</strong>
                ) : (
                    '—'
                ),
        },
        {
            title: 'Per tree',
            key: 'perTree',
            render: (_: unknown, r: CompareRow) => (
                <span style={{ fontSize: 12 }}>
                    {r.perTree.map((p) => (
                        <span key={p.treeId} style={{ display: 'inline-block', marginRight: 10 }}>
                            {p.treeId}:{' '}
                            {p.cost != null && Number.isFinite(p.cost) ? `$${Math.round(p.cost).toLocaleString()}` : '—'}
                        </span>
                    ))}
                </span>
            ),
        },
    ];

    const infoAlert =
        defaultCutCostOnCard != null && Number.isFinite(defaultCutCostOnCard) ? (
            <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={
                    <span>
                        {cardUsesCustomOption ? (
                            <>
                                Card uses <strong>{cardOptionLabel}</strong>:{' '}
                                <strong>${Math.round(defaultCutCostOnCard).toLocaleString()}</strong>. Click a row, then{' '}
                                <strong>Use this option</strong> to update the card.
                            </>
                        ) : (
                            <>
                                AI Card total uses <strong>Back</strong> + <strong>default stump cut &amp; haul wood</strong>{' '}
                                (highlighted row): <strong>${Math.round(defaultCutCostOnCard).toLocaleString()}</strong>.
                            </>
                        )}
                    </span>
                }
            />
        ) : null;

    return (
        <DashboardModal
            open={open}
            onClose={onClose}
            theme={theme}
            title={`Compare cut options — ${scenarioLabel}`}
            width={920}
            maskClosable={!loading}
            destroyOnClose
            loading={loading}
            loadingMessage={`Calling Tree Calculator (${TREE_CUT_COMPARE_UNIFIED_OPTIONS.length} combinations per tree)…`}
            className="obd-tree-cut-compare-modal"
            rootClassName="obd-tree-cut-compare-modal-root"
            modalStyles={{
                body: { overflow: 'hidden', padding: 0, maxHeight: 'min(78vh, 720px)' },
            }}
            footer={
                <>
                    <Button onClick={onClose} disabled={loading}>
                        Close
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleApply}
                        disabled={loading || !pendingRow || pendingRow.total == null}
                    >
                        Use this option
                    </Button>
                </>
            }
        >
            <div className="obd-tree-cut-compare-modal__inner">
                <div className="obd-tree-cut-compare-modal__intro">
                {infoAlert}
                {error ? <Alert type="warning" showIcon message={error} style={{ marginBottom: 12 }} /> : null}
                <Typography.Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
                    All combinations of <strong>site location</strong> (Excel B3) and <strong>stump / haul</strong> presets (
                    {TREE_CUT_COMPARE_UNIFIED_OPTIONS.length} rows). Click a row, then <strong>Use this option</strong> to update
                    the scenario card.
                </Typography.Paragraph>
                </div>
                {!loading ? (
                    <div className="obd-tree-cut-compare-modal__table-wrap">
                    <Table<CompareRow>
                        size="small"
                        pagination={false}
                        columns={columns}
                        dataSource={rows}
                        rowKey="key"
                        locale={{ emptyText: 'No results' }}
                        onRow={(record) => {
                            const isActive = record.key === activeOptionId;
                            const isDefaultHint =
                                record.isCardDefault && !cardUsesCustomOption && !isActive;
                            return {
                                onClick: () => setPendingOptionId(record.key),
                                className: [
                                    'obd-tree-cut-compare-modal__row',
                                    isActive ? 'obd-tree-cut-compare-modal__row--selected' : '',
                                    isDefaultHint ? 'obd-tree-cut-compare-modal__row--default-hint' : '',
                                ]
                                    .filter(Boolean)
                                    .join(' '),
                            };
                        }}
                    />
                    </div>
                ) : null}
            </div>
        </DashboardModal>
    );
}
